import { create } from "zustand";
import type { OutboundScreenPop } from "@/shared/api/outboundApi";
import type {
  BreakStatusDto,
  CallPayload,
  PhoneInfo,
  PhoneStatus,
  SessionStatus,
  TransferredFrom,
} from "@/shared/api/softphoneApi";
import type { ParsedCaller } from "./callerInfo";

/** Estado do registro SIP. */
export type RegState =
  | "idle" // UA parado (agente offline)
  | "connecting" // abrindo WebSocket / primeiro REGISTER
  | "registered"
  | "reconnecting" // caiu e o JsSIP/backoff está tentando de novo
  | "failed"; // falha definitiva (credencial/ramal) — exige ação

/** Dono do softphone entre abas (Web Locks): só uma aba registra e manda heartbeat. */
export type TabOwnership = "pending" | "this" | "other";

export type CallState = "ringing" | "dialing" | "active";

export type ConsultLeg = {
  target: string;
  state: "dialing" | "active";
};

export type CallInfo = {
  id: string;
  direction: "incoming" | "outgoing";
  state: CallState;
  remote: ParsedCaller;
  /** Call-ID SIP da perna principal. */
  sipCallId: string | null;
  /** Token do softphone (`<uuid>.0`) embutido no From das chamadas efetuadas (correlação com o dial-out). */
  softphoneCallId: string | null;
  createdAt: number;
  answeredAt: number | null;
  muted: boolean;
  held: boolean;
  queueFrom: TransferredFrom | null;
  payload: CallPayload[];
  consult: ConsultLeg | null;
  transferring: boolean;
};

export type CallResult = "answered" | "missed" | "rejected" | "failed" | "transferred" | "canceled";

export type HistoryEntry = {
  id: string;
  direction: "incoming" | "outgoing";
  remoteLabel: string;
  remoteUser: string;
  startedAt: number;
  answeredAt: number | null;
  endedAt: number;
  result: CallResult;
  cause?: string;
  queue?: string | null;
  campaign?: string | null;
};

export type PendingApproval = { breakId: number; title: string; requestedAt: number };

export type ScreenPopEntry = OutboundScreenPop & { receivedAt: number };

export type AudioPrefs = { inputId: string; outputId: string; ringerId: string };

export type MicPermission = "unknown" | "granted" | "denied" | "prompt" | "unsupported";

type SoftphoneState = {
  ownership: TabOwnership;
  phone: PhoneInfo | null;
  phoneLoaded: boolean;
  phoneError: string | null;

  reg: RegState;
  regError: string | null;
  regAttempt: number;

  sessionStatus: SessionStatus;
  /** Para PAUSED: `value` da pausa (vira `sessionInfo` no heartbeat/relatórios). */
  sessionInfo: string | null;
  currentBreak: BreakStatusDto | null;
  sessionSince: number;
  pendingApproval: PendingApproval | null;

  call: CallInfo | null;
  history: HistoryEntry[];
  screenPop: ScreenPopEntry | null;

  audio: AudioPrefs;
  micPermission: MicPermission;
  lastError: string | null;
  forcedLogout: boolean;

  patch: (p: Partial<SoftphoneState>) => void;
  patchCall: (p: Partial<CallInfo>) => void;
  setSession: (status: SessionStatus, info: string | null, brk?: BreakStatusDto | null) => void;
  addHistory: (h: HistoryEntry) => void;
  setAudio: (p: Partial<AudioPrefs>) => void;
  reset: () => void;
};

const AUDIO_KEY = "pm.softphone.audio";
const HISTORY_KEY = "pm.softphone.history";
const MAX_HISTORY = 50;

function loadAudio(): AudioPrefs {
  try {
    const raw = localStorage.getItem(AUDIO_KEY);
    if (raw) {
      const v = JSON.parse(raw) as Partial<AudioPrefs>;
      return { inputId: v.inputId ?? "", outputId: v.outputId ?? "", ringerId: v.ringerId ?? "" };
    }
  } catch {
    /* sem storage */
  }
  return { inputId: "", outputId: "", ringerId: "" };
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    const v = raw ? (JSON.parse(raw) as HistoryEntry[]) : [];
    return Array.isArray(v) ? v.slice(0, MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

function saveHistory(h: HistoryEntry[]) {
  try {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(h));
  } catch {
    /* ignore */
  }
}

const initial = () => ({
  ownership: "pending" as TabOwnership,
  phone: null,
  phoneLoaded: false,
  phoneError: null,
  reg: "idle" as RegState,
  regError: null,
  regAttempt: 0,
  sessionStatus: "OFFLINE" as SessionStatus,
  sessionInfo: null,
  currentBreak: null,
  sessionSince: Date.now(),
  pendingApproval: null,
  call: null,
  history: loadHistory(),
  screenPop: null,
  audio: loadAudio(),
  micPermission: "unknown" as MicPermission,
  lastError: null,
  forcedLogout: false,
});

export const useSoftphoneStore = create<SoftphoneState>((set, get) => ({
  ...initial(),

  patch: (p) => set(p),

  patchCall: (p) => {
    const call = get().call;
    if (!call) return;
    set({ call: { ...call, ...p } });
  },

  setSession: (status, info, brk = null) => {
    const s = get();
    const changed = s.sessionStatus !== status || s.sessionInfo !== info;
    set({
      sessionStatus: status,
      sessionInfo: info,
      currentBreak: status === "PAUSED" ? brk : null,
      sessionSince: changed ? Date.now() : s.sessionSince,
    });
  },

  addHistory: (h) => {
    const history = [h, ...get().history.filter((x) => x.id !== h.id)].slice(0, MAX_HISTORY);
    saveHistory(history);
    set({ history });
  },

  setAudio: (p) => {
    const audio = { ...get().audio, ...p };
    try {
      localStorage.setItem(AUDIO_KEY, JSON.stringify(audio));
    } catch {
      /* ignore */
    }
    set({ audio });
  },

  reset: () => {
    try {
      sessionStorage.removeItem(HISTORY_KEY);
    } catch {
      /* ignore */
    }
    set({ ...initial(), history: [] });
  },
}));

/** Status do aparelho exatamente como o backend espera (`tools/PhoneStatus`). */
export function derivePhoneStatus(s: Pick<SoftphoneState, "reg" | "call">): PhoneStatus {
  if (s.call) {
    switch (s.call.state) {
      case "ringing":
        return "RINGING";
      case "dialing":
        return "DIALING";
      case "active":
        return "INUSE";
    }
  }
  return s.reg === "registered" ? "ONLINE" : "OFFLINE";
}

export function hasActiveCall(): boolean {
  return useSoftphoneStore.getState().call != null;
}

const EMPTY_QUEUES: NonNullable<PhoneInfo["queues"]> = [];

/**
 * Filas do ramal com referência estável. `?? []` dentro do selector cria um array novo a cada
 * leitura; no React 19 isso dispara o erro #185 (maximum update depth) em quem não tem fila.
 */
export function selectPhoneQueues(s: Pick<SoftphoneState, "phone">): NonNullable<PhoneInfo["queues"]> {
  return s.phone?.queues ?? EMPTY_QUEUES;
}
